import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShieldCheck, Sparkles, IndianRupee } from "lucide-react";

const About = () => {
  return (
    <div className="font-body">
      <section className="bg-[#FBF7F0] py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-[#D63031] font-semibold mb-3">
            About Gruham
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C3E50]">
            For mom, by mom.
          </h1>
          <p className="mt-5 text-lg text-[#2C3E50]/85 max-w-2xl mx-auto">
            Comfort, affordability and genuine care — every product we make
            tells a story listened to in a real kitchen.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl sm:text-4xl font-bold">
              Born from a mother's struggle.
            </h2>
            <div className="mt-6 space-y-4 text-[#2C3E50]/85 text-lg leading-relaxed">
              <p>
                It started with a simple observation: our founder's mother spent
                hours daily in a hot kitchen, sacrificing her comfort for her
                family's meals.
              </p>
              <p>
                There was no affordable solution. AC was overkill and too
                expensive. Regular fans didn't work. Evaporative coolers were
                complicated.
              </p>
              <p>
                So we asked 50+ moms what they really needed. The answer was
                simple: a fan that actually cools <em>you</em>, not the whole
                house. Plug-and-play. Affordable. Designed with care.
              </p>
              <p className="font-display text-[#D63031] font-semibold text-xl">
                Gruham was born.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <figure className="rounded-3xl overflow-hidden border border-[#F39C12]/25 shadow-[0_18px_40px_-24px_rgba(44,62,80,0.35)] bg-white">
              <img
                data-testid="founder-image"
                src="/img/founder-with-mother.png"
                alt="Gruham founders with their mother"
                loading="lazy"
                className="w-full h-full object-cover aspect-[4/5]"
              />
              <figcaption className="px-4 py-3 text-sm text-[#2C3E50]/85 bg-[#FFF5E1]">
                The founders with their mother — the reason Gruham exists.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#FFF5E1]/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">
            Our mission.
          </h2>
          <p className="mt-6 text-lg sm:text-xl text-[#2C3E50]/85 leading-relaxed">
            Empower Indian women with affordable, practical comfort solutions
            that celebrate and support their daily contributions. Every product
            is designed listening to real mothers, tested by real families, and
            backed by genuine care.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center">
            Why choose Gruham?
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Heart,
                title: "Made by Women, For Women",
                body: "We understand the kitchen because we live it.",
              },
              {
                icon: ShieldCheck,
                title: "Real Testing, Real Results",
                body: "Tested by 50+ moms for months, not focus groups.",
              },
              {
                icon: IndianRupee,
                title: "Affordable Excellence",
                body: "Premium quality. Without the premium price tag.",
              },
              {
                icon: Sparkles,
                title: "Genuine Care",
                body: "Every review, every return, handled with respect.",
              },
            ].map((v, idx) => {
              const Icon = v.icon;
              return (
                <div
                  key={idx}
                  className="bg-white border border-[#F39C12]/20 rounded-2xl p-6 lift-on-hover"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#FFF5E1] flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#D63031]" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#2C3E50]">
                    {v.title}
                  </h3>
                  <p className="text-sm text-[#2C3E50]/80 mt-2">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#D63031] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">
            Have questions? We'd love to hear.
          </h2>
          <p className="mt-4 text-white/90">
            Reach out by email, phone or WhatsApp — we usually reply within a
            day.
          </p>
          <div className="mt-7">
            <Link
              to="/track-order"
              data-testid="about-contact-cta"
              className="inline-flex items-center justify-center rounded-full h-12 px-7 bg-white text-[#D63031] font-semibold hover:bg-[#FFFBF5] transition"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
